import { apiClient } from '@core/api/ApiClientFactory';

// ============ TYPES ============
export interface HybridLocDailyUsageData {
    date: string;
    count: number;
}

export interface HybridLocDetailedUsageData {
    date: string;
    count: number;
}

export interface HybridLocDistributionData {
    shopName: string;
    countApp: number;
    countCoin: number;
}

export interface HybridLocFilterArgs {
    shopId?: string;
    shopIds?: string[];
    start: string;
    end: string;
}

// ============ SERVICE IMPLEMENTATION ============

export const hybridLocService = {
    // HybridLoc - Daily Usage
    async loadDailyUsage(shopId: string, start: string, end: string): Promise<HybridLocDailyUsageData[]> {
        try {
            console.log(`[HybridLocService] Loading daily usage for shop: ${shopId}`);
            const response = await apiClient.get<HybridLocDailyUsageData[]>(
                `hybridloc/usage/daily/${shopId}/${start}/${end}`
            );
            console.log(`[HybridLocService] Daily usage loaded:`, response.data);

            // Sort data by date (newest first)
            const sortedData = response.data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

            return sortedData;
        } catch (error) {
            console.error('[HybridLocService] Error loading daily usage:', error);
            throw error;
        }
    },

    // HybridLoc - Detailed Usage
    async loadDetailedUsage(shopId: string, start: string, end: string): Promise<HybridLocDetailedUsageData[]> {
        try {
            console.log(`[HybridLocService] Loading detailed usage for shop: ${shopId}`);
            const response = await apiClient.get<HybridLocDetailedUsageData[]>(
                `hybridloc/usage/detailed/${shopId}/${start}/${end}`
            );
            console.log(`[HybridLocService] Detailed usage loaded:`, response.data);

            // Sort data by date (newest first)
            const sortedData = response.data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

            return sortedData;
        } catch (error) {
            console.error('[HybridLocService] Error loading detailed usage:', error);
            throw error;
        }
    },

    // HybridLoc - Distribution Usage (App vs Coin)
    async loadDistributionUsage(start: string, end: string, shopIds: string[]): Promise<HybridLocDistributionData[]> {
        try {
            console.log(`[HybridLocService] Loading distribution usage for shops:`, shopIds);
            const shopQuery = shopIds.map((id) => `shopId=${id}`).join('&');
            const response = await apiClient.get<HybridLocDistributionData[]>(
                `hybridloc/usage/distribution/${start}/${end}?${shopQuery}`
            );
            console.log(`[HybridLocService] Distribution usage loaded:`, response.data);
            return response.data;
        } catch (error) {
            console.error('[HybridLocService] Error loading distribution usage:', error);
            throw error;
        }
    },

    // Helper methods for data processing

    /**
     * Calculate percentage for data labels
     */
    calculatePercentage(value: number, total: number): string {
        if (total === 0) return '0%';
        return `${Math.round((value / total) * 100)}%`;
    },

    /**
     * Sum array property values
     */
    sumArrayProperty(array: any[], property: string): number {
        return array.reduce((sum, item) => sum + (item[property] || 0), 0);
    },

    /**
     * Process series data for charts
     */
    processSeriesData(data: HybridLocDistributionData[], propertyName: string): any[] {
        const series: any[] = [];

        data.forEach((element) => {
            const total = element.countApp + element.countCoin;
            const seriesName = this.getTranslatedPropertyName(propertyName);
            const seriesIndex = series.findIndex((s) => s.name === seriesName);
            const dataPoint = {
                x: element.shopName,
                y: element[propertyName as keyof HybridLocDistributionData],
                total
            };

            if (seriesIndex > -1) {
                series[seriesIndex].data.push(dataPoint);
            } else {
                series.push({
                    name: seriesName,
                    data: [dataPoint]
                });
            }
        });

        return series;
    },

    /**
     * Get translated property name (this would use your i18n in actual implementation)
     */
    getTranslatedPropertyName(propertyName: string): string {
        const translations: Record<string, string> = {
            countApp: 'App Usage',
            countCoin: 'Coin Usage'
        };
        return translations[propertyName] || propertyName;
    },

    /**
     * Generate chart options for distribution charts
     */
    generateDistributionChartOptions(data: HybridLocDistributionData[]): any {
        const maxTotal = Math.max(...data.map((item) => item.countApp + item.countCoin));

        return {
            dataLabels: {
                formatter: (value: number, series: any) => {
                    const total = series.w.config.series[series.seriesIndex].data[series.dataPointIndex].total;
                    return [value, this.calculatePercentage(value, total)];
                }
            },
            chart: {
                toolbar: false,
                stacked: true
            },
            xaxis: {
                type: 'category'
            },
            yaxis: {
                max: maxTotal > 0 ? maxTotal : 5,
                forceNiceScale: true
            }
        };
    },

    /**
     * Generate total series for distribution summary
     */
    generateTotalSeries(data: HybridLocDistributionData[]): any[] {
        const totalApp = this.sumArrayProperty(data, 'countApp');
        const totalCoin = this.sumArrayProperty(data, 'countCoin');
        const grandTotal = totalApp + totalCoin;

        return [
            {
                name: this.getTranslatedPropertyName('countApp'),
                data: [{ x: 'Total', y: totalApp, total: grandTotal }]
            },
            {
                name: this.getTranslatedPropertyName('countCoin'),
                data: [{ x: 'Total', y: totalCoin, total: grandTotal }]
            }
        ];
    }
};
